/*
    © 2023 EcomGraduates.com
    https://www.ecomgraduates.com
*/
const localStorageKey = 'ecomify_wishlist_v1'
const dnEscapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

/*
    DN 4.2 (Claude) — merchant 2026-07-16: "wishlist buttons on product cards are
    a bit glitchy and not intuative".

    Two real defects were measured on a rendered collection grid and are fixed
    below. The storage key, the stored record shape and the selected-variant
    capture are deliberately IDENTICAL to before — the drawer, the wishlist page
    and the quick-shop all read this exact structure.

    1. SAVE TOOK ~1000ms TO SHOW. The original awaited /products/<handle>.js
       BEFORE touching localStorage or repainting, so a click produced no visible
       response at all for a full network round trip. The heart now paints the new
       state synchronously on click; the fetch still runs, and storage is written
       once it resolves (reverting the paint if it fails).

    2. RAPID CLICKS LOST THEIR PARITY. Every concurrent call read the same stale
       snapshot of the array, so two quick clicks (save then unsave) both saw
       "not saved", both pushed, and the last write won — leaving the product
       saved when the shopper had just clicked it off. An intent map now records
       the shopper's latest wish per handle, wins over storage while a fetch is in
       flight, and is what the resolved fetch honours before writing.
*/

// handle -> the shopper's latest intended state, while a save is still resolving.
const dnWishlistIntent = new Map()
// handles with an in-flight /products/<handle>.js request.
const dnWishlistInFlight = new Set()

const dnIsWishlisted = (handle, wishlist) => dnWishlistIntent.has(handle)
    ? dnWishlistIntent.get(handle)
    : wishlist.some((elem) => elem.handle === handle)

// Paint one button to a known state. Same DOM effects as initWishlist's loop,
// so an optimistic paint and an authoritative repaint can never disagree.
const dnPaintWishlistButton = (btn, isWishlisted) => {
    const label = isWishlisted ? btn.dataset.textRemove : btn.dataset.textAdd
    btn.querySelector('svg')?.setAttribute('fill', isWishlisted ? 'currentColor' : 'none')
    btn.setAttribute('aria-label', label)
    btn.classList.toggle('is-wishlisted', isWishlisted)
    btn.querySelector('[data-wishlist-label]')?.replaceChildren(document.createTextNode(label))
}

// Every button for a handle (a card and the PDP can both be on screen).
const dnPaintWishlistHandle = (handle, isWishlisted) => {
    document.querySelectorAll('.btn-wishlist-add-remove').forEach(btn => {
        if (btn.dataset.productHandle === handle) dnPaintWishlistButton(btn, isWishlisted)
    })
}

// Add or Remove from Wishlist
window.addOrRemoveFromWishlist = async (btn) => {
    const handle = btn.dataset.productHandle
    const stored = JSON.parse(localStorage.getItem(localStorageKey)) || []
    const wasWishlisted = dnIsWishlisted(handle, stored)
    const shouldBeWishlisted = !wasWishlisted

    // Record the wish and show it straight away — this is the whole fix for the
    // "nothing happens when I click" feel.
    dnWishlistIntent.set(handle, shouldBeWishlisted)
    dnPaintWishlistHandle(handle, shouldBeWishlisted)

    if (!shouldBeWishlisted) {
        const next = (JSON.parse(localStorage.getItem(localStorageKey)) || [])
            .filter((elem) => elem.handle !== handle)
        localStorage.setItem(localStorageKey, JSON.stringify(next))
        if (!dnWishlistInFlight.has(handle)) {
            dnWishlistIntent.delete(handle)
            initWishlist()
        }
        return
    }

    // A save is already resolving for this handle; it will honour the new intent.
    if (dnWishlistInFlight.has(handle)) return
    dnWishlistInFlight.add(handle)

    try {
        const response = await fetch(`/products/${handle}.js`)
        if (!response.ok) throw new Error(`wishlist: ${response.status} for ${handle}`)
        const product = await response.json()

        const variants = product.variants.map(variant =>
            ({
                id: variant.id,
                title: variant.title,
                compare_at_price: variant.compare_at_price,
                price: variant.price,
                featured_image: {
                    src: variant.featured_image?.src || null
                }
            })
        )

        // Remember the selected variant if one is chosen (e.g. on the product page)
        let selectedVariantId = null
        const variantInput = document.querySelector('form[action*="/cart/add"] [name="id"]')
        if (variantInput && variantInput.value) selectedVariantId = Number(variantInput.value)
        if (!selectedVariantId) {
            const urlVariant = new URLSearchParams(location.search).get('variant')
            if (urlVariant) selectedVariantId = Number(urlVariant)
        }
        // Only keep it if it actually belongs to this product
        if (selectedVariantId && !product.variants.some(v => v.id === selectedVariantId)) {
            selectedVariantId = null
        }

        // Honour the shopper's latest click, not the one that started this fetch.
        if (dnWishlistIntent.get(handle) === true) {
            const wishlist = JSON.parse(localStorage.getItem(localStorageKey)) || []
            if (!wishlist.some((elem) => elem.handle === handle)) {
                wishlist.push({
                    id: product.id,
                    handle: product.handle,
                    url: product.url,
                    title: product.title,
                    compare_at_price: product.compare_at_price,
                    price: product.price,
                    price_varies: product.price_varies,
                    featured_image: product.featured_image,
                    vendor: product.vendor,
                    type: product.type,
                    time: Date.now(),
                    selected_variant_id: selectedVariantId,
                    variants
                })
                localStorage.setItem(localStorageKey, JSON.stringify(wishlist))
            }
        }
    } catch (error) {
        // Leave storage untouched; the repaint below reverts the optimistic heart.
        console.warn('Wishlist save failed', error)
    } finally {
        dnWishlistInFlight.delete(handle)
        dnWishlistIntent.delete(handle)
        initWishlist()
    }
}

// Remove from wishlist
window.onRemoveWishlistItem = (btn) => {
    let wishlist = JSON.parse(localStorage.getItem(localStorageKey)) || []

    wishlist = wishlist.filter((elem) => elem.handle !== btn.dataset.productHandle)

    localStorage.setItem(localStorageKey, JSON.stringify(wishlist))

    initWishlist()
}

// Init Wishlist
// Note: Can be called from anywhere if an ajax change is made
const initWishlist = () => {
    const wishlist = JSON.parse(localStorage.getItem(localStorageKey)) || []

    document.querySelectorAll('.btn-wishlist-add-remove').forEach(btn => {
        // dnIsWishlisted keeps a still-resolving save from being repainted back
        // to "not saved" by an unrelated initWishlist() call.
        dnPaintWishlistButton(btn, dnIsWishlisted(btn.dataset.productHandle, wishlist))
    })

    document.querySelectorAll('.wishlist-count-badge').forEach(badge => {
        if (wishlist.length === 0) {
            badge.setAttribute('hidden', 'hidden')
        } else {
            badge.removeAttribute('hidden')
            badge.textContent = wishlist.length
        }
    })

    // Photo-only "likes" grid — merchant 2026-07-15. No text/price, tap opens
    // the product (the saved variant, if one was selected when saving).
    const productList = document.querySelector('#offcanvas-wishlist-product-list')
    if (!productList) return

    if (wishlist.length) {
        // newest first
        const items = [...wishlist].sort((a, b) => (b.time || 0) - (a.time || 0))
        productList.innerHTML = items.map((product) => {
            const savedVariant = product.selected_variant_id
                ? (product.variants || []).find(v => v.id === product.selected_variant_id)
                : null
            const targetUrl = savedVariant ? `${product.url}?variant=${savedVariant.id}` : product.url
            const imgSrc = (savedVariant && savedVariant.featured_image && savedVariant.featured_image.src)
                ? savedVariant.featured_image.src
                : product.featured_image
            const alt = dnEscapeHtml(product.title) + (savedVariant ? ' — ' + dnEscapeHtml(savedVariant.title) : '')
            return `
                <li class="dn-wishlist-tile">
                    <a class="dn-wishlist-tile__link" href="${targetUrl}" aria-label="View ${dnEscapeHtml(product.title)}">
                        <img class="dn-wishlist-tile__img"
                            src="${Shopify.resizeImage(imgSrc || 'no-image.gif', '400x400', 'center')}"
                            alt="${alt}" width="400" height="400" loading="lazy">
                    </a>
                    <button class="dn-wishlist-tile__remove" type="button"
                        data-product-handle="${product.handle}"
                        onclick="onRemoveWishlistItem(this)"
                        aria-label="Remove ${dnEscapeHtml(product.title)} from saved">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                </li>
            `
        }).join('')

        document.querySelector('#offcanvas-wishlist-empty')?.setAttribute('hidden', 'hidden')
        productList.removeAttribute('hidden')
    } else {
        document.querySelector('#offcanvas-wishlist-empty')?.removeAttribute('hidden')
        productList.setAttribute('hidden', 'hidden')
        productList.innerHTML = ''
    }
}
initWishlist()

window.addEventListener('updated.ecomify.cart', initWishlist)
window.addEventListener('updated.ecomify.collection', initWishlist)
window.addEventListener('init.ecomify.recommended_products', initWishlist)
